// React hook for forecast calculations with real failure data and population accounting

import { useState, useMemo } from 'react';
import { FailureFilters } from '@/types/filters';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { createLogger } from '@/utils/logger';
import {
  ForecastResult,
  ForecastConfig,
  SimulationMetadata,
  TurbineForForecast
} from '@/types/forecast';
import { calculateForecast as calculateForecastWithMethod } from '@/utils/forecastIntegration';
import { useFailures } from '@/hooks/useFailures';
import { FailureRecord } from '@/types/failure';
import { FORECAST_CALCULATION_CONSTANTS } from '@/constants/forecastCalculation';
import { usePopulationAnalysis } from '@/hooks/usePopulationAnalysis';
import { useProgressTracking } from '@/hooks/useProgressTracking';
import { useValidationErrors } from '@/hooks/useValidationErrors';

interface UseForecastCalculationProps {
  sourceAnalysis: WeibullAnalysisResult | null;
  filters: FailureFilters;
  configuration: ForecastConfig;
  enabled?: boolean;
}

interface ForecastCalculationResult {
  // Results
  forecastResults: ForecastResult[];
  populationSize: number;  // Initial Weibull analysis population
  forecastSubsetSize: number;  // Subset being forecasted
  simulationMetadata: SimulationMetadata | null;
  
  // Loading states
  isCalculating: boolean;
  turbinesLoading: boolean;
  calculationProgress: number; // Progress percentage (0-100)
  progressText: string; // Current progress description
  
  // Validation
  canCalculate: boolean;
  validationErrors: string[];
  
  // Actions
  calculateForecast: () => Promise<void>;
  clearResults: () => void;
  setForecastResults: (results: ForecastResult[], metadata?: SimulationMetadata) => void;
  
  // Debug info
  _turbineData?: FailureRecord[];
  _turbinesForForecast?: TurbineForForecast[];
}

const logger = createLogger('ForecastCalculation');

export const useForecastCalculation = ({
  sourceAnalysis,
  filters,
  configuration
}: UseForecastCalculationProps): ForecastCalculationResult => {
  // State
  const [forecastResults, setForecastResults] = useState<ForecastResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [simulationMetadata, setSimulationMetadata] = useState<SimulationMetadata | null>(null);

  // Custom hooks
  const { calculationProgress, progressText, createProgressCallback, resetProgress } = useProgressTracking();

  // Get turbine population data using the same API as Weibull analysis
  const turbineFilters = {
    ...filters,
    size: FORECAST_CALCULATION_CONSTANTS.TURBINE_FETCH_SIZE,
    page: 1,
    excludedCaseIds: []
  };

  const {
    data: turbineData,
    isLoading: turbinesLoading
  } = useFailures(turbineFilters);

  // Population analysis using custom hook
  const { populationSize, forecastSubsetSize, turbinesForForecast, validationErrors: populationErrors } = usePopulationAnalysis({
    turbineData,
    sourceAnalysis,
    filters
  });

  // Validation errors using custom hook
  const { validationErrors } = useValidationErrors(populationErrors);

  // Calculate if we can perform forecast
  const canCalculate = useMemo(() => {
    return !!
      sourceAnalysis &&
      populationSize > 0 &&
      populationErrors.length === 0 &&
      !turbinesLoading &&
      !isCalculating;
  }, [sourceAnalysis, populationSize, populationErrors, turbinesLoading, isCalculating]);

  const calculateForecast = async () => {
    if (!canCalculate || !sourceAnalysis || populationSize === 0) {
      logger.warn('Cannot calculate forecast: validation failed');
      return;
    }

    setIsCalculating(true);
    resetProgress();
    setSimulationMetadata(null);

    // Allow React to render the loading screen before starting heavy calculation
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    });

    try {
      const startTime = Date.now();
      const _onProgress = createProgressCallback();
      // Use calculateForecastWithBestMethod with configuration
      const forecastResult = await calculateForecastWithMethod(
        turbinesForForecast,
        sourceAnalysis.parameters,
        sourceAnalysis.mleResult,
        {
          horizonYears: configuration.horizonYears,
          granularityMonths: configuration.granularityMonths,
          forceMonteCarloMethod: configuration.useMonteCarloSimulation,
          preferParameterUncertainty: true
        }
      );

      const results = forecastResult.results;

      if (results.length === 0) {
        throw new Error('Forecast calculation returned no results');
      }

      const computationTime = Date.now() - startTime;

      // Extract simulation metadata from results
      const firstResult = results[0];

      // Detect method used based on available MLE data and results
      const hasMLEResult = sourceAnalysis.mleResult?.fisherInformation?.determinant > 0;
      const hasSimulationData = firstResult?.simulationRuns && firstResult.simulationRuns > 0;

      let method: 'traditional' | 'monte-carlo' | 'parametric-bootstrap' = 'traditional';
      if (configuration.useMonteCarloSimulation && hasMLEResult) {
        method = 'parametric-bootstrap';
      } else if (configuration.useMonteCarloSimulation) {
        method = 'monte-carlo';
      }

      if (hasSimulationData && firstResult) {
        setSimulationMetadata({
          totalRuns: firstResult.simulationRuns,
          convergenceAchieved: firstResult.convergenceAchieved,
          computationTimeMs: computationTime,
          method,
          parameterUncertaintyUsed: method === 'parametric-bootstrap'
        });
      } else {
        setSimulationMetadata({
          computationTimeMs: computationTime,
          method,
          parameterUncertaintyUsed: false
        });
      }

      setForecastResults(results);

    } catch (err) {
      logger.error('Forecast calculation failed:', err);
      setSimulationMetadata(null);
    } finally {
      setIsCalculating(false);
      resetProgress();
    }
  };

  const clearResults = () => {
    setForecastResults([]);
    setSimulationMetadata(null);
    resetProgress();
  };

  const setForecastResultsWithMetadata = (results: ForecastResult[], metadata?: SimulationMetadata) => {
    setForecastResults(results);
    if (metadata) {
      setSimulationMetadata(metadata);
    }
    // Clear any calculation state when setting pre-calculated results
    setIsCalculating(false);
  };

  return {
    // Results
    forecastResults,
    populationSize,
    forecastSubsetSize,
    simulationMetadata,

    // Loading states
    isCalculating,
    turbinesLoading,
    calculationProgress,
    progressText,

    // Validation
    canCalculate,
    validationErrors,

    // Actions
    calculateForecast,
    clearResults,
    setForecastResults: setForecastResultsWithMetadata,

    // Debug info
    _turbineData: turbineData?.failures,
    _turbinesForForecast: turbinesForForecast.slice(0, 5)
  };
};
